import * as React from 'react';
import { RouteComponentProps, useParams } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useQuery } from '@apollo/client';

import { VALUATION_BROKER_QUERY } from '../../../../client/__graphql__/queries';

import { useFormTransition } from '../../../utils/hooks/useFormTransition';

import { Section } from '../../layout/Section';
import { Container } from '../../layout/Container';
import { Heading } from '../../ui/text/Heading';
import { Paragraph } from '../../ui/text/Paragraph';

import { AccordionGeneral } from '../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../partials/AccordionBenefits';

import { Thanks } from '../../layout/Thanks';
import { useFadeTransition } from '../../../utils/hooks/useFadeTransition';
import { BackgroundLoad } from '../../layout/BackgroundLoad';

import { Seo } from '../../../utils/seo/Index';
import { AboutValuationText } from '../../partials/AboutValuationText';
import { useValuationContext } from '../../../utils/hooks/useValuationContext';
import { ValuationForm } from '../../partials/forms/Valuation';
import { PageType } from '../../../__types__/generated/globalTypes';

interface IRoute {
  broker: string;
  path: string;
}

const ValuationBroker: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { posted, init } = useValuationContext();
  const params = useParams<{ broker: string }>();

  React.useEffect(() => {
    init({
      pageType: PageType.MEGLER
    });
  }, []);

  const { data, loading: loadingBroker }: any = useQuery(
    VALUATION_BROKER_QUERY,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          path: match.params.broker
        }
      }
    }
  );

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  return (
    <>
      <Seo
        title={`${data?.getBroker?.name} - PrivatMegleren - Nordeas Eiendomsmeglerkjede`}
        description={`Vi skreddersyr boligsalget til nettopp din bolig. Kontakt meg for e-takst eller verdivurdering. ${params.broker}/verdivurdering-selge-eiendom`}
        image=""
        url={location.pathname}
      />

      <Container style={{ paddingTop: '100px', ...pageFadeIn }}>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h1" center={true}>
              Skal du selge eiendom?
            </Heading>
            <Paragraph center={true} className="form-text">
              Vurderer du å selge eiendommen din? Lurer du på hva egen eiendom
              er verdt, hva det koster å selge eiendommen eller hva du bør gjøre
              før du skal selge?
            </Paragraph>
            <Paragraph center={true} className="form-text">
              Fyll ut skjemaet, så avtaler vi gjerne en tid for å vise hvordan
              din eiendom kan presenteres gjennom PrivatMegleren.
            </Paragraph>
          </Section>
          <ValuationForm />
          {!loadingBroker && data && data.getBroker && data.getBroker.id ? (
            <Section className="form-section">
              <Paragraph>
                Du kan alternativt nå meg på telefon{' '}
                <a href={`tel:${data.getBroker?.cellphone?.replace(/ /g, '')}`}>
                  {data.getBroker.cellphone}
                </a>
              </Paragraph>
            </Section>
          ) : null}
          <Section className="form-section">
            <AboutValuationText />
          </Section>
          <Section className="form-section">
            <AccordionBenefits />
          </Section>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg innen kort tid for å
              avtale en befaring.
            </Paragraph>
            <Paragraph center={true}>
              I mellomtiden kan du lese mer om salg av eiendom her.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
        <BackgroundLoad
          url="https://cdn.reeltime.no/pm_assets/img/bakgrunner/falk_bakside2.jpg"
          opacity={0.3}
        />
      </Container>
    </>
  );
};

export default ValuationBroker;

const FormWrapper = styled(animated.div)``;
